/*
  Ministry Checkup - Admin creates surveys and invites users; respondents take surveys.
  Admin: create survey, select users, view completion and results.
  Respondents: see surveys they're invited to, take survey, save progress, submit.
*/
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useUser } from '@clerk/clerk-react';
import { get } from '../api/client';

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const MinistryCheckupPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [surveys, setSurveys] = useState([]);
  const [mySurveys, setMySurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('surveys');
  const isAdmin = user?.publicMetadata?.role === 'admin';

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        if (isAdmin) {
          const { ok, data } = await get('/api/surveys');
          if (ok) setSurveys(Array.isArray(data) ? data : []);
        }
        const { ok, data } = await get('/api/surveys/my');
        if (ok) setMySurveys(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [isAdmin]);

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title="Ministry Checkup" section="leaders" />
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
        Evaluate ministry health. Admins create surveys and track completion. Respondents take surveys and rate each section.
      </p>

      {isAdmin && (
        <div style={{ marginBottom: '1.5rem' }}>
          <Link to="/ministry-checkup/new">
            <button className="btn-primary" style={{ width: 'auto', padding: '0.65rem 1.25rem' }}>
              Create Survey
            </button>
          </Link>
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => setActiveTab('surveys')}
                style={{
                  padding: '0.4rem 0.8rem',
                  border: activeTab === 'surveys' ? '2px solid var(--cr-button)' : '1px solid var(--cr-border)',
                  borderRadius: '6px',
                  background: activeTab === 'surveys' ? 'var(--cr-bg-warm)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                All Surveys
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('my')}
                style={{
                  padding: '0.4rem 0.8rem',
                  border: activeTab === 'my' ? '2px solid var(--cr-button)' : '1px solid var(--cr-border)',
                  borderRadius: '6px',
                  background: activeTab === 'my' ? 'var(--cr-bg-warm)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                My Surveys
              </button>
            </div>
          )}

          {(activeTab === 'my' || !isAdmin) && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Surveys you can take</h3>
              {mySurveys.length === 0 ? (
                <p style={{ color: 'var(--cr-text-muted)' }}>No surveys at this time.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {mySurveys.map((s) => (
                    <div
                      key={s._id}
                      style={{
                        padding: '1rem',
                        background: 'var(--cr-bg-warm)',
                        borderRadius: '8px',
                        border: '1px solid var(--cr-border)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <strong>{s.title}</strong>
                        <span style={{ marginLeft: '0.5rem', color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>
                          {formatDate(s.surveyDate)}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.9rem' }}
                        onClick={() => navigate(`/ministry-checkup/${s._id}/take`)}
                      >
                        Take Survey
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {isAdmin && activeTab === 'surveys' && (
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>All surveys</h3>
              {surveys.length === 0 ? (
                <p style={{ color: 'var(--cr-text-muted)' }}>No surveys yet. Create one to get started.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {surveys.map((s) => {
                    const invitedCount = (s.invitedUserIds || []).length;
                    const completedCount = s.completedCount ?? 0;
                    const allComplete = invitedCount > 0 && completedCount >= invitedCount;
                    return (
                    <div
                      key={s._id}
                      style={{
                        padding: '1rem',
                        background: 'var(--cr-bg-warm)',
                        borderRadius: '8px',
                        border: '1px solid var(--cr-border)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <strong>{s.title}</strong>
                          <span style={{ marginLeft: '0.5rem', color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>
                            {formatDate(s.surveyDate)}
                          </span>
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                            ({invitedCount} invited · {completedCount}/{invitedCount} completed)
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className="btn-outline"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.9rem' }}
                            onClick={() => navigate(`/ministry-checkup/${s._id}/completion`)}
                          >
                            {allComplete ? 'Completed' : 'Status'}
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.9rem' }}
                            onClick={() => navigate(`/ministry-checkup/${s._id}/results`)}
                          >
                            Results
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MinistryCheckupPage;
